import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  TabList,
  Tab,
  Chip,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Switch,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import {
  Plus as AddIcon,
  ArrowsClockwise as SyncIcon,
  Trash as DeleteIcon,
  Gear as SettingsIcon,
  Robot as AIIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { localSEOError } from '../../lib/localSEOUtils';
import type { GBPPost, MarketingSettings, GoogleBusinessProfile } from '../../types/marketing';

// Component imports
import { CreateGBPPostModal } from '../../components/marketing/CreateGBPPostModal';
import { EditGBPPostModal } from '../../components/marketing/EditGBPPostModal';
import { GBPPostPreview } from '../../components/marketing/GBPPostPreview';
import { GBPPostsTable } from '../../components/marketing/GBPPostsTable';
import { CitationsTable } from '../../components/marketing/CitationsTable';
import { BacklinksTable } from '../../components/marketing/BacklinksTable';
import { GBPAutoSettingsModal } from '../../components/marketing/GBPAutoSettingsModal';
import { GBPProfileSettings } from '../../components/marketing/GBPProfileSettings';
import { RankingHeatmapTab } from '../../components/marketing/rankings/RankingHeatmapTab';

interface LocalSEOStats {
  directory_status?: string;
  backlink_placements?: number;
  backlink_orders_pending?: number;
  keywords_scanned?: number;
  gbp_posts_failed?: number;
  gbp_posts_pending_review?: number;
  citation_count: number;
  citation_verified: number;
  backlink_count: number;
  backlink_active: number;
  keyword_count: number;
  keywords_ranking: number;
  gbp_posts_count: number;
  gbp_posts_scheduled: number;
  gbp_posts_posted: number;
}

export const LocalSEO: React.FC = () => {
  const [actionError, setActionError] = useState('');
  const [publicationMessage, setPublicationMessage] = useState('');
  const [previewPost, setPreviewPost] = useState<GBPPost | null>(null);
  const [profileDirty, setProfileDirty] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('profile');
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [showEditPostModal, setShowEditPostModal] = useState(false);
  const [showAutoSettingsModal, setShowAutoSettingsModal] = useState(false);
  const [editingPost, setEditingPost] = useState<GBPPost | null>(null);
  const [publishingPostId, setPublishingPostId] = useState<number | null>(null);
  const [postsSearch, setPostsSearch] = useState('');
  const [postsStatus, setPostsStatus] = useState('');
  const [postsProfile, setPostsProfile] = useState('');
  const [postsPage, setPostsPage] = useState(1);
  const [postsSortField, setPostsSortField] = useState('created_at');
  const [postsSortDir, setPostsSortDir] = useState<'asc' | 'desc'>('desc');
  const queryClient = useQueryClient();

  // Let React Query distinguish unavailable data from a genuinely empty account.
  const profilesQuery = useQuery({
    queryKey: ['gbp-profiles'],
    queryFn: async () => (await api.getAllPages<GoogleBusinessProfile>('/api/marketing/gbp-profiles/')).data,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
  const profiles = profilesQuery.data || [];
  const selectedProfile = profiles.find(profile => profile.id === selectedProfileId) || profiles[0];

  // Fetch GBP posts
  const postsOrdering = `${postsSortDir === 'desc' ? '-' : ''}${postsSortField}`;
  const postsQuery = useQuery({
    queryKey: ['gbp-posts', postsPage, postsOrdering, postsSearch, postsStatus, postsProfile],
    queryFn: async () => {
      const response = await api.get<{ count: number; results: GBPPost[] }>(
        `/api/marketing/gbp-posts/?page=${postsPage}&page_size=20&ordering=${postsOrdering}&search=${encodeURIComponent(postsSearch)}&status=${postsStatus}&profile=${postsProfile}`
      );
      const data = response.data;
      return Array.isArray(data) ? { count: data.length, results: data as GBPPost[] } : data;
    },
    refetchInterval: query => query.state.data?.results?.some(post => post.status === 'publishing') ? 5000 : false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
  const { data: postsResponse, isLoading: postsLoading } = postsQuery;
  const posts = postsResponse?.results || [];
  const postsTotalPages = Math.max(1, Math.ceil((postsResponse?.count || 0) / 20));

  const handlePostsSort = (field: string) => {
    if (field === postsSortField) {
      setPostsSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setPostsSortField(field);
      setPostsSortDir('desc');
    }
    setPostsPage(1);
  };

  // Publish post mutation
  const publishPostMutation = useMutation({
    mutationFn: async (postId: number) => {
      const response = await api.post(`/api/marketing/gbp-posts/${postId}/publish/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gbp-posts'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      setPublicationMessage('Post queued for publishing. Waiting for confirmation from Google.');
    },
    onError: (error: unknown) => {
      console.error('Failed to publish post:', error);
      setPublishingPostId(null);
      setActionError(localSEOError(error, 'Unable to publish this post.'));
    }
  });

  const publicationQuery = useQuery({
    queryKey: ['gbp-post-publication', publishingPostId],
    queryFn: async () => (await api.get<GBPPost>(`/api/marketing/gbp-posts/${publishingPostId}/`)).data,
    enabled: publishingPostId !== null && !publishPostMutation.isPending,
    refetchInterval: 3000,
  });
  useEffect(() => {
    const post = publicationQuery.data;
    if (!post || publicationQuery.dataUpdatedAt < publishPostMutation.submittedAt || publishPostMutation.isPending || !['posted', 'failed'].includes(post.status)) return;
    setPublicationMessage(post.status === 'posted' ? 'Google confirmed publication.' : 'Publication failed. Review the error and retry when ready.');
    if (post.status === 'failed') setActionError(post.error_message || 'Google could not publish this post.');
    setPublishingPostId(null);
    queryClient.invalidateQueries({queryKey: ['gbp-posts']});
    queryClient.invalidateQueries({queryKey: ['local-seo-stats']});
  }, [publicationQuery.data, publicationQuery.dataUpdatedAt, publishPostMutation.submittedAt, publishPostMutation.isPending, queryClient]);

  const handlePublishPost = (post: GBPPost) => {
    if (window.confirm(`Are you sure you want to publish "${post.title}" now?`)) {
      setActionError('');
      setPublishingPostId(post.id);
      publishPostMutation.mutate(post.id);
    }
  };

  // Fetch marketing settings
  const settingsQuery = useQuery({
    queryKey: ['marketing-settings'],
    queryFn: async () => (await api.get<MarketingSettings>('/api/marketing/settings/')).data,
    staleTime: 5 * 60 * 1000,
  });
  const marketingSettings = settingsQuery.data;

  // Toggle auto-generation mutation
  const toggleAutoGenerationMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      // First get the settings to get the ID
      const settingsResponse = await api.get<MarketingSettings>('/api/marketing/settings/');
      const settingsId = settingsResponse.data.id;

      // Then patch with the ID
      const response = await api.patch(`/api/marketing/settings/${settingsId}/`, {
        auto_generation_enabled: enabled
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-settings'] });
    }
  });

  // Fetch local SEO stats
  const statsQuery = useQuery({
    queryKey: ['local-seo-stats'],
    queryFn: async () => (await api.get<LocalSEOStats>('/api/marketing/local-seo/stats/')).data,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
  const { data: stats, isLoading: statsLoading } = statsQuery;

  // Sync GBP profile mutation
  const syncProfileMutation = useMutation({
    mutationFn: async (profileId: number) => {
      return api.post(`/api/marketing/gbp-profiles/${profileId}/sync/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['gbp-posts'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
    }
  });

  const handleSyncProfile = (profileId: number) => {
    syncProfileMutation.mutate(profileId);
  };

  // Disconnect GBP profile mutation
  const disconnectProfileMutation = useMutation({
    mutationFn: async (profileId: number) => {
      // Call the API endpoint to disconnect
      return api.post(`/api/marketing/gbp-profiles/${profileId}/disconnect/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['gbp-posts'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
    }
  });

  const handleDisconnectProfile = (profileId: number) => {
    if (window.confirm('Are you sure you want to disconnect this Google Business Profile?')) {
      disconnectProfileMutation.mutate(profileId);
    }
  };

  const handleCreatePost = async (postData: Partial<GBPPost>) => {
    try {
      await api.post('/api/marketing/gbp-posts/', postData);
      queryClient.invalidateQueries({ queryKey: ['gbp-posts'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      setShowCreatePostModal(false);
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };

  const handleEditPost = (post: GBPPost) => {
    setEditingPost(post);
    setShowEditPostModal(true);
  };

  const handleUpdatePost = async (postId: number, postData: Partial<GBPPost>) => {
    try {
      const response = await api.patch(`/api/marketing/gbp-posts/${postId}/`, postData);
      queryClient.invalidateQueries({ queryKey: ['gbp-posts'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      setShowEditPostModal(false);
      setEditingPost(null);
      console.log('Post updated:', response.data);
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  };

  const handleDeletePost = async (post: GBPPost) => {
    if (window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
      try {
        await api.delete(`/api/marketing/gbp-posts/${post.id}/`);
        queryClient.invalidateQueries({ queryKey: ['gbp-posts'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
        if (posts.length === 1 && postsPage > 1) setPostsPage(page => page - 1);
      } catch (error) {
        setActionError(localSEOError(error, 'Unable to delete this post.'));
      }
    }
  };

  // Show loading state while stats are loading
  if (statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const defaultStats: LocalSEOStats = {
    citation_count: 0,
    citation_verified: 0,
    backlink_count: 0,
    backlink_active: 0,
    keyword_count: 0,
    keywords_ranking: 0,
    gbp_posts_count: 0,
    gbp_posts_scheduled: 0,
    gbp_posts_posted: 0,
  };

  const safeStats = stats || defaultStats;

  return (
    <Box sx={{ width: '100%' }}>
      {actionError && <Alert color="danger" sx={{mb:2}} endDecorator={<Button variant="plain" color="danger" onClick={() => setActionError('')}>Dismiss</Button>}>{actionError}</Alert>}
      {publicationMessage && <Alert color="neutral" sx={{mb:2}}>{publicationMessage}</Alert>}
      {publicationQuery.isError && <Alert color="warning" sx={{mb:2}} endDecorator={<Button onClick={() => publicationQuery.refetch()}>Check publication status</Button>}>Publication status is temporarily unavailable. Do not submit the post again.</Alert>}
      {[['profiles', profilesQuery], ['posts', postsQuery], ['settings', settingsQuery], ['statistics', statsQuery]].map(([label, query]) => {
        const result = query as typeof profilesQuery;
        return result.isError ? (
          <Alert key={label as string} color="danger" sx={{ mb: 2 }} endDecorator={
            <Button size="sm" variant="outlined" color="danger" onClick={() => result.refetch()}>Retry {label as string}</Button>
          }>Unable to load {label as string}. Your saved data has not been removed.</Alert>
        ) : null;
      })}
      <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" gap={2} sx={{mb:2}}>
        <Box><Typography level="h2">Local SEO</Typography><Typography level="body-sm">Manage your local presence and see what needs attention across all locations.</Typography></Box>
        <Button variant="outlined" loading={statsQuery.isFetching} onClick={() => {
          for (const key of ['local-seo-stats','gbp-posts','gbp-profiles','ranking-keywords','yext-status','backlink-orders']) queryClient.invalidateQueries({queryKey:[key]});
        }}>Refresh overview</Button>
      </Stack>
      {!statsQuery.isError && <>
        <Box sx={{display:'grid',gridTemplateColumns:{xs:'repeat(2,minmax(0,1fr))',md:'repeat(4,minmax(0,1fr))'},gap:2,mb:2}}>
          {[
            {label:'Directory sync',value:(safeStats.directory_status || 'not_connected').replaceAll('_',' '),detail:'Yext connection status',tab:'citations'},
            {label:'Backlink placements',value:safeStats.backlink_placements ?? 0,detail:`${safeStats.backlink_orders_pending ?? 0} orders in process`,tab:'backlinks'},
            {label:'Tracked keywords',value:safeStats.keyword_count,detail:`${safeStats.keywords_scanned ?? 0} with completed scans`,tab:'rankings'},
            {label:'Google Business posts',value:safeStats.gbp_posts_count,detail:`${safeStats.gbp_posts_scheduled} scheduled`,tab:'posts'},
          ].map(card => <Card key={card.tab} variant="outlined"><Typography level="title-sm">{card.label}</Typography><Typography level="h3" sx={{textTransform:'capitalize',overflowWrap:'anywhere'}}>{card.value}</Typography><Typography level="body-sm">{card.detail}</Typography><Button size="sm" variant="plain" onClick={() => setActiveTab(card.tab)}>Manage {card.label.toLowerCase()}</Button></Card>)}
        </Box>
        <Card variant="soft" sx={{mb:3}}>
          <Typography level="title-lg">Next actions</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {!profilesQuery.isPending && !profilesQuery.isError && !profiles.some(profile => profile.is_active) && <Button variant="outlined" onClick={() => setActiveTab('profile')}>Connect a business profile</Button>}
            {(safeStats.gbp_posts_failed ?? 0) > 0 && <Button color="danger" variant="soft" onClick={() => {setActiveTab('posts');setPostsStatus('failed');setPostsSearch('');setPostsProfile('');setPostsPage(1);}}>Resolve {safeStats.gbp_posts_failed} failed posts</Button>}
            {(safeStats.gbp_posts_pending_review ?? 0) > 0 && <Button variant="soft" onClick={() => {setActiveTab('posts');setPostsStatus('pending_review');setPostsSearch('');setPostsProfile('');setPostsPage(1);}}>Review {safeStats.gbp_posts_pending_review} pending posts</Button>}
            {safeStats.directory_status !== 'synced' && <Button variant="outlined" onClick={() => setActiveTab('citations')}>Check directory sync</Button>}
            {safeStats.keyword_count === 0 && <Button variant="outlined" onClick={() => setActiveTab('rankings')}>Add your first ranking keyword</Button>}
            <Button variant="plain" onClick={() => setActiveTab('profile')}>Review business information</Button>
          </Stack>
          <Typography level="body-xs">Directory sync reports the Yext connection, not verification on every directory. Placements count recorded live URLs.</Typography>
        </Card>
      </>}
      {/* Main Content Tabs */}
      <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value as string)} sx={{ width: '100%' }}>
        <TabList
          sx={{
            width: '100%',
            flexWrap: 'wrap',
            '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } },
          }}
        >
          <Tab value="profile">Profile Settings</Tab>
          <Tab value="posts">Posts</Tab>
          <Tab value="citations">Citations</Tab>
          <Tab value="backlinks">Backlinks</Tab>
          <Tab value="rankings">Rankings</Tab>
        </TabList>
      </Tabs>

      {/* Tab Panels */}
      {activeTab === 'profile' && (
        <Box sx={{ mt: 2 }}>
          {profilesQuery.isPending ? <CircularProgress aria-label="Loading profiles" /> : profilesQuery.isError ? null : profiles.length > 0 ? (
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Business location</FormLabel>
                <Select value={selectedProfile.id} onChange={(_, value) => {
                  if (value !== null && (!profileDirty || window.confirm("Discard unsaved changes and switch location?"))) setSelectedProfileId(value);
                }}>
                  {profiles.map(profile => <Option key={profile.id} value={profile.id}>{profile.location_name}{profile.is_active ? '' : ' · Disconnected'}</Option>)}
                </Select>
              </FormControl>
              <Typography level="body-sm">{selectedProfile.address}</Typography>
              {!selectedProfile.is_active && <Alert color="warning">This location is disconnected. Reconnect Google Business Profile before publishing posts.</Alert>}
              <GBPProfileSettings key={selectedProfile.id} profileId={selectedProfile.id} onDirtyChange={setProfileDirty} />
            </Stack>
          ) : (
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography level="body-lg" sx={{ mb: 2 }}>
                  No Google Business Profile connected
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
                  Connect your Google Business Profile to manage your business information, posts, and local SEO.
                </Typography>
                <Button
                  variant="solid"
                  color="primary"
                  size="lg"
                  onClick={async () => {
                    try {
                      const response = await api.get<{ oauth_url: string }>('/api/marketing/gbp-profiles/connect/');
                      if (response && response.data && response.data.oauth_url) {
                        window.location.href = response.data.oauth_url;
                      }
                    } catch (error) {
                      console.error('Failed to initiate GBP OAuth:', error);
                    }
                  }}
                >
                  Connect Google Business Profile
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 'business-profile-removed' && (
        <Box sx={{ mt: 2 }}>
          {/* Google Business Profile Section */}
          <Box sx={{ mb: 3 }}>
            <Typography level="h3" sx={{ mb: 2 }}>Google Business Profiles</Typography>
            {profiles.length > 0 ? (
              <Stack spacing={2}>
                {profiles.map((profile: GoogleBusinessProfile) => (
                  <Card key={profile.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography level="h4">{profile.location_name}</Typography>
                          <Typography level="body-sm">{profile.address}</Typography>
                          <Typography level="body-sm">{profile.phone}</Typography>
                          {profile.website && (
                            <Typography level="body-sm">
                              <a href={profile.website} target="_blank" rel="noopener noreferrer">
                                {profile.website}
                              </a>
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await api.get('/api/marketing/gbp/diagnose/');
                                console.log('GBP Diagnostic Results:', response);

                                if (response.data && typeof response.data === 'object' && 'solution' in response.data) {
                                  const solution = (response.data as { solution: { issue: string; steps: string[] } }).solution;
                                  const message = `Issue: ${solution.issue}\n\nSteps to resolve:\n${solution.steps.join('\n')}`;
                                  alert(message);
                                } else {
                                  alert('Diagnostic completed. Check console for detailed results.');
                                }
                              } catch (error) {
                                console.error('Error running GBP diagnostics:', error);
                                alert('Failed to run diagnostics. Please check the console for details.');
                              }
                            }}
                          >
                            Diagnose
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            startDecorator={<SyncIcon />}
                            onClick={() => handleSyncProfile(profile.id)}
                            loading={syncProfileMutation.isPending}
                          >
                            Sync
                          </Button>
                          <IconButton
                            variant="outlined"
                            color="danger"
                            onClick={() => handleDisconnectProfile(profile.id)}
                            disabled={disconnectProfileMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography level="body-lg" sx={{ mb: 2 }}>
                    No Google Business Profiles connected
                  </Typography>
                  <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
                    Connect your Google Business Profile to manage posts, track reviews, and monitor your online presence.
                  </Typography>
                  <Button
                    variant="solid"
                    color="primary"
                    size="lg"
                    onClick={async () => {
                      try {
                        const response = await api.get<{ oauth_url: string }>('/marketing/gbp/connect/');
                        if (response && response.data && response.data.oauth_url) {
                          window.location.href = response.data.oauth_url;
                        }
                      } catch (error) {
                        console.error('Failed to initiate GBP OAuth:', error);
                        // You might want to show an error message here
                      }
                    }}
                  >
                    Connect Google Business Profile
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Export SEO Report Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => console.log('Export SEO Report')}
            >
              Export SEO Report
            </Button>
          </Box>
        </Box>
      )}

      {activeTab === 'posts' && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 1.5, md: 3 },
              }}
            >
              <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Google Business Posts
              </Typography>

              {/* Auto-generation controls */}
              <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                <Switch
                  checked={marketingSettings?.auto_generation_enabled || false}
                  onChange={(event) => {
                    if (profiles.length === 0 && event.target.checked) {
                      alert('Please connect a Google Business Profile first before enabling auto-generation.');
                      return;
                    }
                    toggleAutoGenerationMutation.mutate(event.target.checked);
                  }}
                  disabled={toggleAutoGenerationMutation.isPending || !marketingSettings || profilesQuery.isError}
                  startDecorator={<AIIcon />}
                  endDecorator={
                    marketingSettings?.auto_generation_enabled && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => setShowAutoSettingsModal(true)}
                      >
                        <SettingsIcon />
                      </IconButton>
                    )
                  }
                />
                <FormLabel>Auto-Generate Posts</FormLabel>
              </FormControl>

              {marketingSettings?.auto_generation_enabled && (
                <Chip
                  size="sm"
                  color="success"
                  variant="soft"
                >
                  {marketingSettings.generation_frequency === 'daily' ? 'Daily' :
                   marketingSettings.generation_frequency === 'weekly' ? 'Weekly' :
                   marketingSettings.generation_frequency === 'biweekly' ? 'Twice Weekly' :
                   'Custom Schedule'}
                </Chip>
              )}
            </Box>

            <Button
              variant="solid"
              startDecorator={<AddIcon />}
              onClick={() => setShowCreatePostModal(true)}
              disabled={!profiles.some(profile => profile.is_active)}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              Create Post
            </Button>
          </Box>

          <CollapsibleFilters><Stack direction={{xs: 'column', md: 'row'}} gap={1.5} sx={{mb: 2}}>
            <FormControl sx={{flex: 2}}><FormLabel>Search posts</FormLabel><Input value={postsSearch} placeholder="Title or content" onChange={event => {setPostsSearch(event.target.value); setPostsPage(1);}} /></FormControl>
            <FormControl sx={{flex: 1}}><FormLabel>Post status</FormLabel><Select value={postsStatus} onChange={(_, value) => {setPostsStatus(value || ''); setPostsPage(1);}}>
              <Option value="">All statuses</Option>
              {['draft','pending_review','scheduled','publishing','posted','failed'].map(value => <Option key={value} value={value}>{value.replace('_',' ')}</Option>)}
            </Select></FormControl>
            <FormControl sx={{flex: 1}}><FormLabel>Post location</FormLabel><Select value={postsProfile} onChange={(_, value) => {setPostsProfile(value || ''); setPostsPage(1);}}>
              <Option value="">All locations</Option>
              {profiles.map(profile => <Option key={profile.id} value={String(profile.id)}>{profile.location_name}</Option>)}
            </Select></FormControl>
            {(postsSearch || postsStatus || postsProfile) && <Button variant="plain" onClick={() => {setPostsSearch(''); setPostsStatus(''); setPostsProfile(''); setPostsPage(1);}}>Clear filters</Button>}
          </Stack></CollapsibleFilters>
          {!postsQuery.isError && <Typography level="body-sm" sx={{mb: 1}}>{postsResponse?.count ?? 0} matching posts</Typography>}

          {profilesQuery.isError || postsQuery.isError ? null : profiles.length === 0 ? (
            <Alert color="neutral">
              Connect a Google Business Profile first to create posts.
            </Alert>
          ) : postsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <GBPPostsTable
                posts={posts}
                filtered={!!(postsSearch || postsStatus || postsProfile)}
                onPreview={setPreviewPost}
                onPublish={handlePublishPost}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
                isPublishing={publishingPostId}
                sortField={postsSortField}
                sortDirection={postsSortDir}
                onSort={handlePostsSort}
              />
              {postsTotalPages > 1 && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    disabled={postsPage === 1}
                    onClick={() => setPostsPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Typography level="body-sm">Page {postsPage} of {postsTotalPages}</Typography>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    disabled={postsPage === postsTotalPages}
                    onClick={() => setPostsPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {activeTab === 'citations' && (
        <Box sx={{ mt: 2 }}>
          <CitationsTable />
        </Box>
      )}

      {activeTab === 'backlinks' && (
        <Box sx={{ mt: 2 }}>
          <BacklinksTable />
        </Box>
      )}

      {activeTab === 'rankings' && (
        <Box sx={{ mt: 2 }}>
          <RankingHeatmapTab />
        </Box>
      )}


      <Modal open={!!previewPost} onClose={() => setPreviewPost(null)}><ModalDialog sx={{width:'min(560px, 94vw)',maxHeight:'90vh',overflow:'auto'}}><ModalClose />{previewPost && <GBPPostPreview post={previewPost}/>}</ModalDialog></Modal>
      {showCreatePostModal && profiles.length > 0 && <CreateGBPPostModal
        open={showCreatePostModal}
        onClose={() => setShowCreatePostModal(false)}
        profiles={profiles.filter(profile => profile.is_active)}
        initialProfileId={selectedProfile?.id}
        onSubmit={handleCreatePost}
      />}

      <EditGBPPostModal
        open={showEditPostModal}
        onClose={() => {
          setShowEditPostModal(false);
          setEditingPost(null);
        }}
        post={editingPost}
        onSubmit={handleUpdatePost}
      />

      <GBPAutoSettingsModal
        open={showAutoSettingsModal}
        onClose={() => setShowAutoSettingsModal(false)}
        settings={marketingSettings ?? null}
        onSave={async (updatedSettings) => {
          try {
            // First get the settings to get the ID
            const settingsResponse = await api.get<MarketingSettings>('/api/marketing/settings/');
            const settingsId = settingsResponse.data.id;

            // Then patch with the ID
            await api.patch(`/api/marketing/settings/${settingsId}/`, updatedSettings);
            queryClient.invalidateQueries({ queryKey: ['marketing-settings'] });
            setShowAutoSettingsModal(false);
          } catch (error) {
            console.error('Error saving settings:', error);
          }
        }}
      />
    </Box>
  );
};
